import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.samples');
import { OnInit, OnDestroy } from '@angular/core';
import { Service } from "src/libs/portal/season/service";
import { Router, NavigationEnd } from '@angular/router';

declare const WizRoute: any;

@Component({
    selector: 'wiz-page-samples',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.samples/view.scss */
:host {
  display: block;
  height: 100%;
}`],
})
export class PageSamplesComponent implements OnInit, OnDestroy {
    public loading = true;
    public samples: string[] = [];
    public perSampleSummary: any = {};
    public activeSample: string = '';
    public activeShort: string = '';
    public detail: any = null;
    public selectedRunIdx = 0;
    public imageDetail: any = null;
    public perImageLoading = false;
    public crossAnalysis: any = null;
    public crossAnalysisLoading = false;
    public expandedFinding: { [k: string]: boolean } = {};
    private routerSub: any;

    constructor(@Inject( Service) public service: Service,@Inject( Router)  private router: Router) { }

    public async ngOnInit() {
        await this.service.init();

        const overview = await wiz.call("overview");
        if (overview.code === 401) {
            location.href = "/";
            return;
        }
        if (overview.code === 200) {
            this.samples = overview.data.samples || [];
            this.perSampleSummary = overview.data.per_sample || {};
        }

        await this.resolveActive();
        await this.loadDetail();

        this.routerSub = this.router.events.subscribe(async (event: any) => {
            if (event instanceof NavigationEnd) {
                const prev = this.activeSample;
                await this.resolveActive();
                if (prev !== this.activeSample) {
                    this.selectedRunIdx = 0;
                    await this.loadDetail();
                }
            }
        });

        this.loading = false;
        await this.service.render();
    }

    public ngOnDestroy() {
        if (this.routerSub) this.routerSub.unsubscribe();
    }

    private short(name: string): string {
        const idx = name.search(/26AN\d+/);
        if (idx < 0) return name;
        const end = name.slice(idx).search(/[^\w]/);
        return end < 0 ? name.slice(idx) : name.slice(idx, idx + end);
    }

    public suffix(name: string): string {
        const code = this.short(name);
        return name.replace(code, '').trim();
    }

    private async resolveActive() {
        const seg = WizRoute?.segment?.sample;
        if (seg) {
            const match = this.samples.find(s => this.short(s) === seg || s === decodeURIComponent(seg));
            this.activeSample = match || this.samples[0] || '';
        } else {
            this.activeSample = this.samples[0] || '';
        }
        this.activeShort = this.short(this.activeSample);
    }

    public async selectSample(name: string) {
        this.service.href('/samples/' + encodeURIComponent(this.short(name)));
    }

    public async loadDetail() {
        if (!this.activeSample) return;
        this.loading = true;
        await this.service.render();
        const { code, data } = await wiz.call("detail", { sample: this.activeSample });
        if (code === 200) this.detail = data;
        this.loading = false;
        this.imageDetail = null;
        await this.loadImageDetail();
        await this.loadCrossAnalysis();
    }

    public async selectRun(idx: number) {
        this.selectedRunIdx = idx;
        this.imageDetail = null;
        await this.service.render();
        await this.loadImageDetail();
    }

    public async loadImageDetail() {
        const run = this.activeRun;
        if (!run) return;
        this.perImageLoading = true;
        await this.service.render();
        const { code, data } = await wiz.call("image_detail", {
            sample: this.activeSample,
            timestamp: run.timestamp,
        });
        if (code === 200) this.imageDetail = data;
        this.perImageLoading = false;
        await this.service.render();
    }

    public get runs(): any[] {
        return this.detail?.runs || [];
    }

    public get activeRun(): any {
        return this.runs[this.selectedRunIdx] || null;
    }

    public get perImage(): any {
        return this.imageDetail?.per_image || null;
    }

    public get i3Points(): number[] {
        return this.runs.map(r => r.I3 ?? 0);
    }

    public get ascPoints(): number[] {
        return this.runs.map(r => r.ASC ?? 0);
    }

    public get vehiclePoints(): number[] {
        return this.runs.map(r => r.vehicle ?? 0);
    }

    // Chart scaling (shared x=run index, y=normalized 0..1)
    public plotYRange(values: number[], pad = 0.1): [number, number] {
        if (!values.length) return [0, 1];
        const lo = Math.min(...values), hi = Math.max(...values);
        const p = (hi - lo) * pad || 1;
        return [lo - p, hi + p];
    }

    public linePath(values: number[], width: number, height: number, range: [number, number]): string {
        if (!values.length) return '';
        const [lo, hi] = range;
        const span = hi - lo || 1;
        const step = values.length > 1 ? width / (values.length - 1) : 0;
        return values.map((v, i) => {
            const x = i * step;
            const y = height - ((v - lo) / span) * height;
            return (i === 0 ? 'M' : 'L') + x.toFixed(1) + ',' + y.toFixed(1);
        }).join(' ');
    }

    public cxcy(values: number[], idx: number, width: number, height: number, range: [number, number]): { x: number; y: number } {
        const [lo, hi] = range;
        const span = hi - lo || 1;
        const step = values.length > 1 ? width / (values.length - 1) : 0;
        return { x: idx * step, y: height - ((values[idx] - lo) / span) * height };
    }

    public fmt(v: any, d = 1): string {
        return v == null ? '-' : Number(v).toFixed(d);
    }

    // histogram bar height (normalized to max count)
    public histBars(hist: any, width: number, height: number): any[] {
        if (!hist || !hist.counts) return [];
        const max = Math.max(...hist.counts) || 1;
        const barW = hist.counts.length > 0 ? width / hist.counts.length : 0;
        return hist.counts.map((c: number, i: number) => ({
            x: i * barW,
            y: height - (c / max) * height,
            w: Math.max(barW - 1, 0.5),
            h: (c / max) * height,
            count: c,
            edge: hist.edges[i],
        }));
    }

    // combined histogram scale for vehicle + asc
    public overlayRange(r: any): [number, number] {
        const veh = r?.labels?.vehicle?.csp_hist;
        const asc = r?.labels?.ASC?.csp_hist;
        if (!veh || !asc) return [0, 1];
        const lo = Math.min(veh.edges[0], asc.edges[0]);
        const hi = Math.max(veh.edges[veh.edges.length - 1], asc.edges[asc.edges.length - 1]);
        return [lo, hi];
    }

    public overlayBars(hist: any, range: [number, number], width: number, height: number, maxCount: number): any[] {
        if (!hist || !hist.counts) return [];
        const [lo, hi] = range;
        const span = hi - lo || 1;
        return hist.counts.map((c: number, i: number) => {
            const eLo = hist.edges[i];
            const eHi = hist.edges[i + 1];
            const x1 = ((eLo - lo) / span) * width;
            const x2 = ((eHi - lo) / span) * width;
            const h = (c / maxCount) * height;
            return { x: x1, y: height - h, w: Math.max(x2 - x1 - 0.5, 0.5), h, count: c };
        });
    }

    public overlayMaxCount(r: any): number {
        const veh = r?.labels?.vehicle?.csp_hist?.counts || [];
        const asc = r?.labels?.ASC?.csp_hist?.counts || [];
        return Math.max(...veh, ...asc, 1);
    }

    // Per-image comparison helpers
    public imgCspDelta(labelKey: string, imgA: string, imgB: string): number | null {
        const grp = this.perImage?.[labelKey];
        if (!grp) return null;
        const muA = grp[imgA]?.CSP?.mean;
        const muB = grp[imgB]?.CSP?.mean;
        if (muA == null || muB == null || muA === 0) return null;
        return Math.abs(muA - muB) / ((muA + muB) / 2) * 100;
    }

    public imgDeltaClass(pct: number | null): string {
        if (pct == null) return '';
        if (pct > 20) return 'text-red-600 font-semibold';
        if (pct > 10) return 'text-amber-600 font-medium';
        return 'text-emerald-600';
    }

    public imgHistBars(label: string, img: string, range: [number, number], width: number, height: number, maxCount: number): any[] {
        const hist = this.perImage?.[label]?.[img]?.CSP?.hist;
        if (!hist) return [];
        return this.overlayBars(hist, range, width, height, maxCount);
    }

    public imgOverlayRange(label: string, imgA: string, imgB: string): [number, number] {
        const hA = this.perImage?.[label]?.[imgA]?.CSP?.hist;
        const hB = this.perImage?.[label]?.[imgB]?.CSP?.hist;
        if (!hA || !hB) return [0, 1];
        const lo = Math.min(hA.edges[0], hB.edges[0]);
        const hi = Math.max(hA.edges[hA.edges.length - 1], hB.edges[hB.edges.length - 1]);
        return [lo, hi];
    }

    public imgOverlayMax(label: string, imgA: string, imgB: string): number {
        const cA = this.perImage?.[label]?.[imgA]?.CSP?.hist?.counts || [];
        const cB = this.perImage?.[label]?.[imgB]?.CSP?.hist?.counts || [];
        return Math.max(...cA, ...cB, 1);
    }

    // Cross-run image analysis
    public async loadCrossAnalysis() {
        if (!this.activeSample) return;
        this.crossAnalysisLoading = true;
        await this.service.render();
        const { code, data } = await wiz.call("image_analysis", { sample: this.activeSample });
        if (code === 200) this.crossAnalysis = data;
        this.crossAnalysisLoading = false;
        await this.service.render();
    }

    public get crossSummary(): any {
        return this.crossAnalysis?.summary || null;
    }

    public get crossPerRun(): any[] {
        return this.crossAnalysis?.per_run || [];
    }

    public get crossFindings(): any[] {
        return this.crossAnalysis?.findings || [];
    }

    public async toggleFinding(id: string) {
        this.expandedFinding[id] = !this.expandedFinding[id];
        await this.service.render();
    }

    public findingSeverityClass(s: string): string {
        const m: any = {
            critical: 'bg-red-100 text-red-800',
            high: 'bg-orange-100 text-orange-800',
            medium: 'bg-yellow-100 text-yellow-800',
            low: 'bg-green-100 text-green-800',
            info: 'bg-blue-100 text-blue-800',
        };
        return m[s] || 'bg-gray-100 text-gray-800';
    }

    public findingSeverityLabel(s: string): string {
        const m: any = { critical: '치명', high: '높음', medium: '중간', low: '낮음', info: '정보' };
        return m[s] || s;
    }

    // Run-level CSP delta points for vehicle and ASC
    public get crossVehDeltas(): number[] {
        return this.crossPerRun.map(r => r.vehicle?.csp_delta ?? 0);
    }

    public get crossAscDeltas(): number[] {
        return this.crossPerRun.map(r => r.ASC?.csp_delta ?? 0);
    }
}

export default PageSamplesComponent;